import { IIndicator, IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management";
import { StateService } from "@uirouter/angularjs";
import { IOrganization, IOrganizationTree } from "@gtmhub/configuration/workday/models";
import { OrganizationsActions } from "@gtmhub/configuration/workday/redux/organizations-actions";
import { WorkdayUserProvisioningService } from "@gtmhub/configuration/workday/workday-user-provisioning-service";
import { Unsubscribe } from "redux";

interface IManualOrganizationMapService {
  teamId: string;
}

interface ConflictingEntityMappingResponse {
  alreadyMappedGtmhubEntityId: string;
}

export class ManualOrganizationTeamMappingModalCtrl {
  params: IManualOrganizationMapService;
  indicators: {
    loading?: IIndicator;
  };
  organizationsFetched: boolean;
  reduxError: IUIError;
  organizationsTree: IOrganizationTree[] = [];
  organizations: IOrganization[] = [];
  searchTerm: string;
  selectedOrganizationId: string;
  teamId: string;
  private unsubscribeFromStore: Unsubscribe;

  static $inject = ["$scope", "$state", "$stateParams", "WorkdayUserProvisioningService", "UIErrorHandlingService", "$ngRedux", "OrganizationsActions"];
  constructor(
    private $scope: IExtendedRootScopeService,
    private $state: StateService,
    private $stateParams: IManualOrganizationMapService,
    private workdayUserProvisioningService: WorkdayUserProvisioningService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $ngRedux: INgRedux,
    private organizationsActions: OrganizationsActions
  ) {}

  $onInit(): void {
    this.indicators = {
      loading: { progress: true },
    };

    this.searchTerm = "";
    this.teamId = this.$stateParams.teamId;

    const bindStateToScope = (state) => {
      return {
        organizations: state.organizations.organizations,
        organizationsFetched: state.organizations.isFetched,
      };
    };
    this.unsubscribeFromStore = this.$ngRedux.connect(bindStateToScope.bind(this.$state))(this);
    this.$ngRedux.dispatch(this.organizationsActions.fetchOrganizations());
    this.$scope
      .watchUntilAndRejectOnError(
        () => this.organizationsFetched,
        () => this.reduxError
      )
      .then(() => {
        delete this.indicators.loading;
      })
      .catch((error) => {
        this.uiErrorHandlingService.handleModal(error);
        delete this.indicators.loading;
      });
    this.$scope.$on("saveModalClicked", () => this.confirmMapping());
  }

  $onDestroy(): void {
    this.unsubscribeFromStore();
  }

  resetIndicator(indicator: keyof ManualOrganizationTeamMappingModalCtrl["indicators"]): void {
    delete this.indicators[indicator];
  }

  setSelectedOrganization = (organizations: string[]): void => {
    if (organizations.length === 0) {
      return;
    }
    this.selectedOrganizationId = organizations[0];
  };

  confirmMapping(): void {
    if (this.selectedOrganizationId && !this.indicators.loading?.["progress"]) {
      this.indicators.loading = { progress: true };
      this.workdayUserProvisioningService
        .mapSingleTeamToOrganization(this.teamId, this.selectedOrganizationId)
        .then(() => {
          this.$scope.$root.$broadcast("mappingSaved", {
            gtmhubTeamId: this.teamId,
            workdayOrganizationId: this.selectedOrganizationId,
          });
          this.$state.go("^.^");
        })
        .catch((error) => {
          if (error.status === 409) {
            this.deleteTeamMapping(error.data as ConflictingEntityMappingResponse, this.selectedOrganizationId);
          } else {
            delete this.indicators.loading;
            this.uiErrorHandlingService.handleModal(error);
          }
        });
    }
  }

  private deleteTeamMapping(conflictReponse: ConflictingEntityMappingResponse, organizationId: string) {
    this.workdayUserProvisioningService
      .deleteTeamToOrganizationMapping(conflictReponse.alreadyMappedGtmhubEntityId)
      .then(() => this.workdayUserProvisioningService.mapSingleTeamToOrganization(this.teamId, organizationId))
      .then(() => {
        this.$scope.$root.$broadcast(
          "mappingSaved",
          {
            gtmhubTeamId: this.teamId,
            workdayOrganizationId: this.selectedOrganizationId,
          },
          true
        );
        this.$state.go("^.^");
      })
      .catch((error) => {
        this.uiErrorHandlingService.handleModal(error);
      });
  }
}
